/* Montlaire storefront bootstrap: local utilities required by theme assets. */
(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const all = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  window.getFocusableElements = window.getFocusableElements || ((container) =>
    all('summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, object, iframe', container)
  );

  let lastFocusedElement = null;
  let focusTrapHandler = null;
  window.trapFocus = window.trapFocus || ((container, elementToFocus = container) => {
    if (!container) return;
    const focusable = window.getFocusableElements(container);
    const first = focusable[0] || container;
    const last = focusable[focusable.length - 1] || first;
    lastFocusedElement = document.activeElement;
    if (focusTrapHandler) document.removeEventListener('keydown', focusTrapHandler);
    focusTrapHandler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', focusTrapHandler);
    try { (elementToFocus || first).focus({ preventScroll: true }); } catch { (elementToFocus || first).focus?.(); }
  });

  window.removeTrapFocus = window.removeTrapFocus || ((elementToFocus = lastFocusedElement) => {
    if (focusTrapHandler) document.removeEventListener('keydown', focusTrapHandler);
    focusTrapHandler = null;
    try { elementToFocus?.focus?.({ preventScroll: true }); } catch { elementToFocus?.focus?.(); }
  });

  window.pauseAllMedia = window.pauseAllMedia || (() => {
    all('.js-youtube').forEach((frame) => frame.contentWindow?.postMessage?.('{"event":"command","func":"pauseVideo","args":""}', '*'));
    all('.js-vimeo').forEach((frame) => frame.contentWindow?.postMessage?.('{"method":"pause"}', '*'));
    all('video').forEach((video) => video.pause?.());
    all('product-model').forEach((model) => model.modelViewerUI?.pause?.());
  });

  window.onKeyUpEscape = window.onKeyUpEscape || ((event) => {
    if (event.code?.toUpperCase() !== 'ESCAPE') return;
    const details = event.target.closest?.('details[open]');
    if (!details) return;
    details.removeAttribute('open');
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    details.querySelector('summary')?.focus?.();
  });

  window.debounce = window.debounce || ((fn, wait = 300) => {
    let timeout;
    return function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  });

  window.fetchConfig = window.fetchConfig || ((type = 'json') => ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  }));
})();


/* V4 header visibility guard. The native header still controls its normal sticky behavior. */
(() => {
  'use strict';
  const syncTopState = () => {
    const atTop = window.scrollY <= 8;
    document.documentElement.classList.toggle('montlaire-page-scrolled', !atTop);
    if (!atTop) return;
    const header = document.querySelector('.section-header');
    if (header) header.classList.remove('shopify-section-header-hidden');
  };
  const install = () => {
    syncTopState();
    requestAnimationFrame(syncTopState);
    setTimeout(syncTopState, 80);
    setTimeout(syncTopState, 300);
    const header = document.querySelector('.section-header');
    if (header && !header.__montlaireVisibilityObserver) {
      const observer = new MutationObserver(() => {
        if (window.scrollY <= 8 && header.classList.contains('shopify-section-header-hidden')) {
          header.classList.remove('shopify-section-header-hidden');
        }
      });
      observer.observe(header, { attributes: true, attributeFilter: ['class'] });
      header.__montlaireVisibilityObserver = observer;
    }
  };
  document.addEventListener('DOMContentLoaded', install, { once: true });
  window.addEventListener('pageshow', install);
  window.addEventListener('scroll', syncTopState, { passive: true });
})();

/* V5: native header fallback + independent Shipping Protection. */
(() => {
  'use strict';
  const q = (sel, root = document) => root.querySelector(sel);
  const qa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function isActuallyVisible(el) {
    if (!el) return false;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) return false;
    const r = el.getBoundingClientRect();
    return r.width > 20 && r.height > 8 && r.bottom > -5;
  }

  function syncHeaderFallback() {
    const fallback = q('#MontlaireLocalHeaderFallback');
    if (!fallback) return;
    const nativeHeader = q('.section-header');
    const nativeTicker = q('.shopify-section-group-header-group .horizontal-ticker');
    const nativeWorks = isActuallyVisible(nativeHeader) && isActuallyVisible(nativeTicker);
    fallback.classList.toggle('is-active', !nativeWorks);
    fallback.setAttribute('aria-hidden', nativeWorks ? 'true' : 'false');
    document.documentElement.classList.toggle('montlaire-local-fallback-active', !nativeWorks);
  }

  function installHeaderFallback() {
    syncHeaderFallback();
    requestAnimationFrame(syncHeaderFallback);
    [80, 250, 700, 1500].forEach((ms) => setTimeout(syncHeaderFallback, ms));
    window.addEventListener('resize', window.debounce ? window.debounce(syncHeaderFallback, 120) : syncHeaderFallback, { passive: true });
    document.addEventListener('shopify:section:load', () => setTimeout(syncHeaderFallback, 50));

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest?.('[data-montlaire-cart-open]');
      if (cartLink && q('cart-drawer')) {
        event.preventDefault();
        const drawer = q('cart-drawer');
        if (typeof drawer.open === 'function') drawer.open(cartLink);
        else {
          drawer.classList.add('active');
          document.body.classList.add('overflow-hidden');
        }
      }
      const closeBtn = event.target.closest?.('.montlaire-local-mobile-menu__close');
      if (closeBtn) {
        event.preventDefault();
        closeBtn.closest('details')?.removeAttribute('open');
      }
    }, true);
  }

  async function getCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js?_=${Date.now()}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
    });
    if (!response.ok) throw new Error(`Cart read failed (${response.status})`);
    return response.json();
  }

  async function writeProtection(variantId, enabled) {
    let cart = await getCart();
    const existing = cart.items.find((item) => Number(item.variant_id) === Number(variantId));
    if (enabled && !existing) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
      });
      if (!response.ok) throw new Error(`Protection add failed (${response.status})`);
    } else if (!enabled && existing) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: existing.key, quantity: 0 })
      });
      if (!response.ok) throw new Error(`Protection remove failed (${response.status})`);
    }
    return getCart();
  }

  function money(cents) {
    try {
      if (window.Shopify?.currency?.active) return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style:'currency', currency: window.Shopify.currency.active }).format(cents / 100);
    } catch (_) {}
    return `$${(cents / 100).toFixed(2)}`;
  }

  function syncCartNumbers(cart) {
    if (!cart) return;
    const totalCents = Number(cart.total_price ?? cart.items_subtotal_price ?? 0);
    const subtotalCents = Number(cart.items_subtotal_price ?? cart.total_price ?? 0);
    const totalFormatted = money(totalCents);
    const subtotalFormatted = money(subtotalCents);

    qa('[data-montlaire-cart-subtotal]').forEach((el) => {
      el.textContent = totalFormatted;
    });

    qa('[data-montlaire-checkout-label], #CartDrawer-Checkout .button__label').forEach((el) => {
      const current = (el.textContent || '').replace(/\s+/g, ' ').trim();
      const base = (current.split('•')[0] || 'Checkout').trim();
      el.textContent = `${base} • ${subtotalFormatted}`;
    });

    qa('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => {
      el.textContent = cart.item_count;
    });
  }

  function stabilizeCartNumbers(cart) {
    syncCartNumbers(cart);
    requestAnimationFrame(() => syncCartNumbers(cart));
    [60, 180, 450].forEach((ms) => setTimeout(() => syncCartNumbers(cart), ms));
  }

  async function refreshDrawerPreservingOpen(cart) {
    const drawer = q('cart-drawer');
    const wasOpen = !!drawer?.classList.contains('active');
    try {
      const root = window.Shopify?.routes?.root || window.routes?.root || '/';
      const url = `${root}cart?sections=cart-drawer,cart-icon-bubble&_=${Date.now()}`;
      const response = await fetch(url, { credentials:'same-origin', headers:{ Accept:'application/json', 'Cache-Control':'no-cache' } });
      if (!response.ok) throw new Error('Section refresh failed');
      const sections = await response.json();
      if (sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = doc.querySelector('cart-drawer');
        const current = q('cart-drawer');
        if (fresh && current) {
          current.replaceWith(fresh);
          if (wasOpen) {
            fresh.classList.add('active');
            document.body.classList.add('overflow-hidden');
          }
        }
      }
      if (sections['cart-icon-bubble']) {
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const fresh = doc.querySelector('#cart-icon-bubble');
        const current = q('#cart-icon-bubble');
        if (fresh && current) current.replaceWith(fresh);
      }
    } catch (error) {
      console.warn('[Montlaire] drawer refresh fallback:', error);
    }
    stabilizeCartNumbers(cart);
    setTimeout(syncShippingProtectionState, 30);
  }

  let protectionBusy = false;
  window.MontlaireShippingProtectionToggle = async function(input, opts = {}) {
    if (!input || protectionBusy) return;
    const variantId = Number(input.dataset.variantId || 0);
    if (!variantId) return;
    protectionBusy = true;
    const enabled = !!input.checked;
    input.disabled = true;
    const host = input.closest('cart-drawer-upsell');
    if (host) host.dataset.selected = String(enabled);
    try {
      if (!opts.auto) {
        if (enabled) localStorage.removeItem('montlaire_shipping_protection_optout');
        else localStorage.setItem('montlaire_shipping_protection_optout', '1');
      }
      const cart = await writeProtection(variantId, enabled);
      stabilizeCartNumbers(cart);
      await refreshDrawerPreservingOpen(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (error) {
      console.error('[Montlaire] Shipping Protection update failed:', error);
      input.checked = !enabled;
      if (host) host.dataset.selected = String(!enabled);
    } finally {
      protectionBusy = false;
      input.disabled = false;
    }
  };

  async function syncShippingProtectionState() {
    const input = q('input[data-montlaire-shipping-protection="true"]');
    if (!input || input.dataset.montlaireSyncing === '1') return;
    input.dataset.montlaireSyncing = '1';
    try {
      const cart = await getCart();
      const variantId = Number(input.dataset.variantId || 0);
      const exists = cart.items.some((item) => Number(item.variant_id) === variantId);
      input.checked = exists;
      const host = input.closest('cart-drawer-upsell');
      if (host) host.dataset.selected = String(exists);
      const shouldDefault = input.dataset.defaultSelected === 'true' && localStorage.getItem('montlaire_shipping_protection_optout') !== '1';
      if (!exists && shouldDefault && cart.item_count > 0 && !protectionBusy) {
        input.checked = true;
        if (host) host.dataset.selected = 'true';
        await window.MontlaireShippingProtectionToggle(input, { auto: true });
      }
    } catch (error) {
      console.warn('[Montlaire] protection state sync skipped:', error);
    } finally {
      const live = q('input[data-montlaire-shipping-protection="true"]');
      if (live) live.dataset.montlaireSyncing = '0';
    }
  }

  function installProtectionObserver() {
    syncShippingProtectionState();
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => Array.from(m.addedNodes || []).some((node) => node.nodeType === 1 && (node.matches?.('cart-drawer, cart-drawer *') || node.querySelector?.('input[data-montlaire-shipping-protection="true"]'))))) {
        setTimeout(syncShippingProtectionState, 40);
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  const boot = () => {
    installHeaderFallback();
    installProtectionObserver();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once:true });
  else boot();
})();
